<template>
  <the-tooltip
    v-bind:is-disabled="!hasEditingRestrict"
    trigger-type="click"
    position="top">
    <template v-slot:trigger="{ listenerObject }">
      <div
        class="visit-service-table__row"
        v-on="listenerObject"
        v-bind:class="{ new: isNew, 'no-hover': hasEditingRestrict }">
        <div
          class="visit-service-table__cell__name"
          v-bind:title="categoryName"> {{ serviceName }}</div>
        <div
          data-label="Зубы"
          class="visit-service-table__cell">
          <popover-menu>
            <template v-slot:trigger="{ listenerObject }">
              <div
                class="visit-service-table__cell tooth"
                v-on="listenerObject"
                v-if="shownToothIcon" />
              <div
                class="visit-service-table__cell"
                v-on="listenerObject"
                v-else>{{teethText}}</div>
            </template>
            <teeth-select-popup
              v-bind:onlyView="onlyView"
              v-on:submit="onSubmitTeeth"
              v-bind:initial-selected="teeth"
              v-bind:initial-dental-parts="dentalParts"
              v-bind:is-milk-bite-default="isMilkBiteDefault" />
          </popover-menu>
        </div>
        <div
          data-label="Кол-во"
          class="visit-service-table__unit">
          <input-count
            class="visit-service-table__cell count edit"
            v-bind:disabled="onlyView"
            v-bind:value="count"
            v-on:input="onInputCount"
            v-on:blur="onInputCount"
          />
        </div>
        <div
          data-label="Цена"
          class="visit-service-table__unit">
          <the-rub
            class="visit-service-table__cell cost pointer"
            data-label="Цена"
            show-kop="auto"
            v-bind:kop="cost" />
        </div>
        <input-checkbox
          class="visit-service-table__cell vhi"
          v-bind:disabled="onlyView"
          v-bind:value="complaint"
          v-on:input="onInputComplaint"
        />
        <input-discount
          class="visit-service-table__cell discount"
          data-label="Скидка"
          v-bind:disabled="isInputDiscountDisabled"
          v-bind:discount-type="discountType"
          v-bind:max-discount-in-kop="amountKop"
          v-bind:value="discountValue"
          v-bind:violations="errorsDiscount"
          v-on:input="onInputDiscount"
        />
        <div
          data-label="Итого"
          class="visit-service-table__cell pointer">
          {{ formattedFinalPrice }}
        </div>
        <div
          data-label="Статус"
          class="visit-service-table__cell pointer">
          {{ paidStatus }}
        </div>
        <div
          v-if="canDelete"
          class="visit-service-table__cell"
          data-label="Удалить">
          <button
            class="visit-service-table__icon-delete"
            type="button"
            v-on:click="onDelete" />
        </div>
      </div>
    </template>
    Услуга уже оплачена. Редактирование недоступно
  </the-tooltip>
</template>

<script>

import InputCheckbox from '@/components/InputCheckbox.vue';
import InputCount from '@/components/InputCount.vue';
import PopoverMenu from '@/components/PopoverMenu.vue';
import TeethGroupMixin from '@/mixins/teethGroupMixin';
import TeethSelectPopup from '@/components/TeethSelectPopup.vue';
import InputDiscount from '@/components/InputDiscount.vue';
import TheRub from '@/components/TheRub.vue';
import TheTooltip from '@/components/TheTooltip.vue';

/**
 * @description Название события удаления строки.
 * @type {string}
 */
const NAME_EVENT_DELETE = 'delete';

export default {
  components: {
    TheTooltip,
    InputCheckbox,
    InputCount,
    InputDiscount,
    PopoverMenu,
    TeethSelectPopup,
    TheRub,
  },
  computed: {
    /**
     * @description Позицию можно удалить.
     * @type {boolean}
     */
    canDelete() {
      return this.canDeletePositions && !this.onlyView;
    },
    /**
     * @description Показывать иконку зуба.
     * @type {boolean}
     */
    shownToothIcon() {
      return this.teeth.length + this.dentalParts.length === 0;
    },
    teethText({ teeth }) {
      const segments = this.groupTeethInSegments(teeth);
      const parts = this.dentalParts.map(({ title }) => title);
      const partSegments = parts.join(', ');
      return [segments, partSegments].filter((segment) => segment !== '').join(', ');
    },
    /**
     * @description Деактивировать поле скидки.
     * @returns {boolean}
     */
    isInputDiscountDisabled() {
      return this.onlyView || this.canChangeDiscount === false;
    },
    /**
     * @description Деактивировать поле ДМС.
     * @returns {boolean}
     */
    isInputByVhiPolicyDisabled() {
      return this.onlyView || this.complaint;
    },
  },
  methods: {
    /**
     * @description Обработать удаление позиции.
     * @returns {void}
     */
    onDelete() {
      this.$emit(NAME_EVENT_DELETE);
    },
    onInputByVhiPolicy(value) {
      this.$emit('input-by-vhi-policy', value);
    },
    onSubmitTeeth(teeth, dentalParts, toothGroupSelected) {
      this.$emit('input-teeth', { teeth, dentalParts });
      this.$nextTick(() => {
        const countTeeth = teeth.length < 1 ? 1 : teeth.length;
        this.onInputCount(toothGroupSelected ? 1 : countTeeth);
      });
    },
    onInputComplaint(complaint) {
      const byVhiPolicy = complaint ? false : this.byVhiPolicy;
      this.$emit('input-complaint', { complaint, byVhiPolicy });
    },
    onInputCount(value) {
      this.$emit('input-count', value);
    },
    onInputDiscount(value) {
      this.$emit('input-discount', value);
    },
  },
  mixins: [
    TeethGroupMixin,
  ],
  props: {
    /**
     * @description Сумма в копейках.
     */
    amountKop: { required: true, type: Number },
    /**
     * @description Может изменять скидки.
     * @type {boolean}
     */
    canChangeDiscount: { default: () => false, type: Boolean },
    /**
     * @description Может удалять позиции.
     * @type {boolean}
     */
    canDeletePositions: { default: () => false, type: Boolean },
    /**
     * @description Только для просмотра.
     * @type {boolean}
     */
    onlyView: { default: () => false, type: Boolean },
    /**
     * @description Базовое состояние прикуса
     * @type {boolean}
     */
    isMilkBiteDefault: { type: Boolean, default: () => false },
    /**
     * @description Нарушения ограничений скидки.
     */
    errorsDiscount: { default: () => {}, type: Array },
    /**
     * @description Услуга оказана по полису ДМС.
     */
    byVhiPolicy: { required: true, type: Boolean },
    /**
     * @description Название категории услуги.
     */
    categoryName: { required: true, type: String },
    /**
     * @description Услуга оказана по гарантии.
     */
    complaint: { required: true, type: Boolean },
    /**
     * @description Стоимость за единицу.
     */
    cost: { required: true, type: Number },
    /**
     * @description Количество.
     */
    count: { required: true, type: Number },
    /**
     * @description Стоматологические части.
     * @type {{value:string,title:string}[]}
     */
    dentalParts: { required: true, type: Array },
    /**
     * @description Тип скидки.
     */
    discountType: { required: true, type: String },
    /**
     * @description Значение скидки.
     */
    discountValue: { required: true, type: Number },
    /**
     * @description Название услуги.
     */
    serviceName: { required: true, type: String },
    /**
     * @description Номера зубов.
     */
    teeth: { required: true, type: Array },
    /**
     * @description Новая услуга.
     */
    isNew: { default: () => false, type: Boolean },
    /**
     * @description Форматированное представление итоговой цены.
     */
    formattedFinalPrice: { required: true, type: String },
    /**
     * @description Статус оплаты.
     */
    paidStatus: { required: true, type: String },
    /**
     * @description Есть запрет на редактирование.
     */
    hasEditingRestrict: { default: () => false, type: Boolean },
  },
};
</script>

<style lang="scss">

.v-autocomplete__content {
  max-width: calc(500px - 74px);
}
.v-list-item__subtitle, .v-list-item__title {
  white-space: pre-wrap !important;
}
@media screen and (max-width: 620px) {
  .v-autocomplete__content {
    max-width: calc(90% - 52px);
  }
}
</style>
